// © Microsoft Corporation. All rights reserved.

import { AppError, createError, PerfTimeline, ResolverAppErrors, logger, tryGetPerfTimeline, CachedConfigsForDifferentFlight, ConfigCrsFetchNon200Response, ConfigCdnFetchNon200Response, ResolverErrorConfigsDownloadedByCdn, SSRAppErrors, GitConfigsInvalidConfigRef, GitConfigsInvalidConfigResolver } from "@msnews/diagnostics";
import { Environment, PageRequestTargetScope, appEnvironment } from "../app";
import { ExperienceConfigWrapper, ExperienceConfigs, getConfigRefString } from "./configs/";
import { IndexedDb, ServerCache, UrlUtility, Utility, getFromWebWorker, isWebWorkerPresent, sendToWebWorker, abortableFetch } from "../utilities";
import { getLocalStorage } from "../webstorage";
import { canUseDOM, getLocationHref, getLocationSearch } from "@msnews/isomorphic-util";
import { isArray, isEmpty, isString, memoize, mergeWith } from "lodash-es";

import { IExperienceConfig } from "cms-models-msn/configuration/IExperienceConfig";
import { IExperienceConfigIndex } from "cms-models-msn/configuration/IExperienceConfigIndex";
import { IExperienceConfigLink } from "cms-models-msn/configuration/IExperienceConfigLink";
import { getPeregrineFlights } from "@msnews/experimentation-helpers";
import { AppConfig, ConfigReference, ExperienceConfig } from "@msnews/config-data-types";
import { ConfigCacheProperties, ConfigFetchError, ConfigServiceParameters, ResolverEnvironment } from "@msnews/core-contracts";
import { Telemetry } from "@msnews/telemetry-contracts";
import { getRootConfigRef } from "@msnews/web-worker-shared-utils";
import { ConfigResolver } from "@msnews/config-resolver";
import { mockManager } from "@msnews/mock-config";

/**
 * Type definition for cached configs in the indexed DB
 */
type CachedConfigsType = Map<string, ExperienceConfigWrapper>;

/**
 * Constants for TTLs
 */
const SERVER_CACHE_TTL: number = 60 * 60 * 24 * 1000; // one day in milliseconds
const SERVER_CACHE_CONFIG_STALENESS_TIMEOUT: number = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Constants for fetch timeouts
 */
const CSRTimeoutInMs = 10000;
const SSRTimeoutInMs = 900;

/**
 * Resolver Class
 */
class Resolver {

    /**
     * Target scope of the current page request
     * @type {PageRequestTargetScope}
     */
    private currentTargetScope: PageRequestTargetScope = appEnvironment.CurrentRequestTargetScope;

    /**
     * Target scope of the current page request override. Only used by unit tests and tooling
     * @type {PageRequestTargetScope}
     */
    private currentTargetScopeOverride: PageRequestTargetScope;

    /**
     * Dictionary to maintain all resolved config for internal state management
     * @type {Map<string, ExperienceConfigWrapper>}
     */
    private configMap: CachedConfigsType = new Map();

    /** set fallback env to prod to avoid any live site issues */
    private env: Environment = appEnvironment.Environment || Environment.prod;

    /**
     * Config base url used during cms config resolution.
     * @type {string}
     */
    private cdnConfigBaseUrl: URL;

    /**
     * Config base url for Config Service
     * @type {string}
     */
    private configServiceBaseUrl: URL;

    /**
     * Dictionary to maintain all promise objects which are not resolved/rejected. Once promise object is resolved/rejected,
     * it gets deleted from this dictionary and put in the configMap.
     * @type {Map<string, Promise<ExperienceConfigWrapper>>}
     */
    private promiseMap: Map<string, Promise<ExperienceConfigWrapper>> = new Map();

    /**
     * Maintain promise objects that is updating stale timestamp for server cache.
     * This is to prevent concurrent getConfig from updating stale cache even when the head config refresh has initiated
     */
    private refreshServerCachePromiseMap: Map<string, Promise<void>> = new Map();

    /** Resolver app errors */
    private resolverErrors: AppError[];

    /** Object of IndexedDb wrapper class */
    private indexedDb: IndexedDb;

    /**
     * Check if IndexedDb operation is allowed or not.
     * @type {boolean}: true if indexedDB should be used else false.
     */
    private useIndexedDb: boolean;

    /**
     * Check if ServerCache can be used or not.
     * @type {boolean}
     */
    private useServerCache: boolean;

    /** Current Timestamp for config staleness check. */
    private stalenessCheckTimestamp: number = new Date().getTime();

    /**
     * Check if ConfigService operation is allowed or not.
     * @type {boolean}
     */
    private enableConfigService: boolean;

    /**
     * Get config service parameters from currentTargetScope
     * @type {string}
     */
    private configServiceParameters: string;

    /**
     * Storage key for indexedDb
     * @type {string}
     */
    private storageKey: string;

    /** Config timings object storing fetch duration of network or cache to retrieve config. */
    private configTimings: ConfigFetchTiming[];

    /**
     * Check if web worker exists
     * @type {boolean}
     */
    private useWebWorkerToCache: boolean;

    /** The perf timeline for the resolver */
    private perfTimeline: PerfTimeline;

    /**
     * Check if config service shold return more than 1 config
     * @type {boolean}
     */
    private isMultiConfigRetrievalEnabled = false;

    /** Maps config index HREF to local override for config. */
    private localOverrideMap: { [configIndexHref: string]: ExperienceConfigWrapper } = {};

    /** Initialize indexedDb for configs. */
    private initializeIndexedDb = memoize(this._initializeIndexedDb);

    /** Initialize configs from IDB. */
    private populateConfigMapFromIndexedDbAsync = memoize(this._populateConfigMapFromIndexedDbAsync);

    /** Peregrine only lower cased flights in alphabetical order */
    private peregrineFlights: string;

    /** Peregrine flights local storage key */
    private peregrineFlightsKey = "prgflights";

    /**
     * Git config resolver class
     */
    private configResolver: ConfigResolver;

    /**
     * Target scope with Peregrine only flights in page experiments
     */
    private scopeWithPeregrineFlights: PageRequestTargetScope;

    /**
     * Constructor to initialize private variables.
     */
    public constructor() {
        this.initializeCaching();
    }

    /**
     * Initialize caching layer of resolver.
     *
     * WARNING!!! Do NOT add async/await in this function. It must remain a pure synchronous function in order
     *      to initialize the indexedDB cache as quickly as possible before entry point beats to us due to multi-level
     *      promise waiting hierarchies.
     *
     * @param idb object of indexedDb class. Unit tests set this value. It will be null in other cases.
     * @
     */
    public initializeCaching(idb?: IndexedDb): void {
        /**
         * In most cases configRootUrl will be sent by PCS so appEnvironment will have it.
         * In case it is not sent, we fall back to environmentConfig so config will be always served.
         */
        this.cdnConfigBaseUrl = appEnvironment.ConfigRootUrl || appEnvironment.EnvironmentConfigUrl;

        // Get ConfigServiceBaseUrl from appEnvironment
        this.configServiceBaseUrl = appEnvironment.ConfigServiceBaseUrl;

        this.resolverErrors = [];
        this.configTimings = [];

        this.useWebWorkerToCache = isWebWorkerPresent;

        this.indexedDb = idb ? idb : new IndexedDb();

        const enableCMSPPEConfigs = appEnvironment.EnableCMSPPEConfigs;

        // Check if IndexedDb is supported
        this.useIndexedDb =
            // Debug scenario: Check if indexedDB is allowed to be read in main thread. By default true.
            this.enableIndexedDbInMainResolver()
            // PCS flag to turn on/off IndexedDb caching of configs.
            && !appEnvironment.DisableCachingConfigs
            // Debug scenario: to turn off indexedDB in all threads.
            && this.indexedDb.isCachingAllowed
            // Check if there exists IndexedDB in browser
            && this.indexedDb.supported
            // Use case is simple config tests, we do not want to cache
            // Also not caching in server-side in this scenario
            && !enableCMSPPEConfigs;

        // Check if server cache could be used or not
        this.useServerCache = ServerCache !== null
            // PCS flag to turn on/off IndexedDb caching of configs.
            && !appEnvironment.DisableCachingConfigs
            // Use case is simple config tests, we do not want to cache
            // Also not caching in server-side in this scenario
            && !enableCMSPPEConfigs;

        this.setTargetScopeAndConfigServiceParams();

        // if pre-initialized config map doesn't exist or does not contain the storageKey entry we want,
        // then proceed to asynchronously initialize the configMap from indexedDb
        // NOTE: we only start the async read, and don't await for this to finish
        this.populateConfigMapFromIndexedDbAsync();
    }

    /**
     * Update target scope as well as updating config service parameters and other related target scope configurations
     * in case that target scope from appEnvironment changed during life cycle of a page.
     */
    public setTargetScopeAndConfigServiceParams() {

        const enableCMSPPEConfigs = appEnvironment.EnableCMSPPEConfigs;

        // Get current target scope in case it has changed. Special case for unit tests where override was set so we don't want to update target scope.
        this.currentTargetScope = this.currentTargetScopeOverride || appEnvironment.CurrentRequestTargetScope;

        // Read peregrine specific flights
        const pageExperiments = this.currentTargetScope && getPeregrineFlights(this.currentTargetScope.pageExperiments);
        if (pageExperiments && pageExperiments.length > 0) {
            this.peregrineFlights = pageExperiments.join(",");
        }

        this.scopeWithPeregrineFlights = {
            ...this.currentTargetScope,
            pageExperiments
        };

        // Check whether to use Config Resolver Service (CRS).
        this.enableConfigService = this.useConfigService();
        if (this.enableConfigService) {

            // Query string parameter from target scope.
            this.configServiceParameters = "?targetScope=" + JSON.stringify(this.scopeWithPeregrineFlights);

            // Send PCS apptype to CRS so we can build CRS Jarvis dashboards per apptype.
            this.configServiceParameters += `&apptype=${appEnvironment.AppType}`;

            const { IsMultiConfigRetrievalEnabled, CacheBustingIdentifier } = appEnvironment;

            // Check if CRS should fetch multiple configs and its depth.
            this.isMultiConfigRetrievalEnabled = IsMultiConfigRetrievalEnabled;
            if (this.isMultiConfigRetrievalEnabled) {
                this.configServiceParameters += `&maxDepth=${this.getConfigRetrievalMaxDepth()}`;
            }

            // If cbid is provided, typically in unit testing context, use it.
            // If window has _cbid then use it else use the value from PCS, to send to CRS for busting disk, AFD and Origin cache.
            const cacheBustingIdentifier = (canUseDOM() && window._cbid) || CacheBustingIdentifier;
            if (cacheBustingIdentifier) {
                this.configServiceParameters += `&cbid=${cacheBustingIdentifier}`;
            }

            // If url has dev only query string params, inform CRS so it can exclude from availability and will help debugging on Jarvis.
            if (this.containsDevOnlyQueryParameters()) {
                this.configServiceParameters += "&item=dev:true";
            }

            if (enableCMSPPEConfigs) {
                this.configServiceParameters += "&item=cms:ppe";
            }
        }

        // Update storage key based on target scope.
        this.storageKey = this.generateKey();
    }

    /**
     * Initialize Git config resolver environment.
     * @param trackAppErrorEvent - App Error function
     */
    public initialize(trackAppErrorEvent: (data: AppError) => void) {

        this.perfTimeline = tryGetPerfTimeline("Resolver", "Resolver", true);

        // Check if this is git configs treatment
        if (!appEnvironment.IsGitConfigs) {
            return;
        }

        // Optionally pre-initialized IDB config read promise at the top page level
        const { _idbConfigOpenPromise = null, _idbConfigReadPromise = null, _idbConfigLookup = null } = canUseDOM() ? window : {};

        const configCacheProperties: ConfigCacheProperties = {
            ...this.getConfigCacheProperties(),
            idbConfigOpenPromise: _idbConfigOpenPromise,
            idbConfigReadPromise: _idbConfigReadPromise,
            idbConfigLookup: _idbConfigLookup
        };

        // Set up config service param contract
        const configServiceParameters: ConfigServiceParameters = {
            buildVersion: appEnvironment.BuildVersion,
            configServiceBaseUrl: appEnvironment.ConfigServiceBaseUrl.toString()
        };

        // Set up Git config resolver contract
        const resolverEnvironment: ResolverEnvironment = {
            appType: appEnvironment.AppType,
            configServiceParameters,
            targetScope: this.scopeWithPeregrineFlights,
            env: appEnvironment.Environment.toString(),
            featureFlags: appEnvironment.FeatureFlags,
            isThisWebWorkerThread: false,
            isWebWorkerPresent: this.useWebWorkerToCache,
            queryParams: UrlUtility.getParamsObj(getLocationSearch()),
            appErrorCallback: trackAppErrorEvent,
            loggerCallback: logger.log.bind(logger),
            logObjectsCallback: logger.logObjects.bind(logger),
            configCacheProperties,
            configOrigins: appEnvironment.ConfigOrigins,
            mockManager
        };

        // Initialize Git configs resolver with resolver environment
        this.configResolver = new ConfigResolver(resolverEnvironment);
    }

    /**
     * Merges config objects.
     * @private
     * @param {ExperienceConfigWrapper[]} configs - an array of config documents
     * @param {string} experienceType - experienceType
     * @return ExperienceConfig - returns merged config based on a set of rules
     */
    public mergeConfig(configs: IExperienceConfig[]): IExperienceConfig {
        if (!configs || configs.length < 1) {
            return null;
        }

        const result: IExperienceConfig = configs[0];

        // skip the 0th item as it is assigned to result above, only merge properties section
        for (let count = 1; count < configs.length; count++) {
            result.properties = mergeWith(result.properties, configs[count].properties, this.customizer);
        }

        return result;
    }

    /**
     * matches targeting scope and returns the config urls for the same.
     * @private
     * @param {IExperienceConfigIndex} configIndex - an array of config documents
     * @return string[] - returns urls for configurations for which the scope matched
     */
    public resolveConfigIndex(configIndex: IExperienceConfigIndex): string[] {
        if (!configIndex) {
            return null;
        }

        const configPaths: string[] = [];
        configIndex.configs.forEach(experienceRef => {
            if (this.targetScopeMatched(experienceRef)) {
                configPaths.push(experienceRef.href);
            }
        });

        // if override config is provided, then we add that to the end of list
        // so props provided in override config will add/update existing properties.
        // This is similar to CMS override settings but scoped to experience level.
        if (configIndex.overrideConfig && Utility.stringHasData(configIndex.overrideConfig.href)) {
            configPaths.push(configIndex.overrideConfig.href);
        }

        return configPaths;
    }

    /**
     * basic version of target scope that only checks market currently.
     * @private
     * @param {IExperienceConfigReference} experienceRef - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope matches with config target scope, otherwise false
     */
    public targetScopeMatched(experienceRef: IExperienceConfigLink): boolean {
        const targetScope = experienceRef.targetScope;

        // if target scope is undefined for a config entry, we treat that as default config and apply it
        if (!targetScope) {
            return true;
        }

        return (this.audienceModeMatched(targetScope)
            && this.browserTypeMatched(targetScope)
            && this.deviceTypeMatched(targetScope)
            && this.domainMatched(targetScope)
            && this.experimentMatched(targetScope)
            && this.marketMatched(targetScope)
            && this.osMatched(targetScope)
            && this.pageTypeMatched(targetScope)
            && this.platformMatched(targetScope));
    }

    /**
     * function to check if current audienceMode type matches target audienceMode type
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope audienceMode matches with config target scope audienceMode, otherwise false
     */
    public audienceModeMatched(targetScope: PageRequestTargetScope): boolean {
        return !targetScope.audienceMode || this.currentTargetScope.audienceMode === targetScope.audienceMode;
    }

    /**
     * function to check if current browser type matches target browser type
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope browser type matches with config target scope browser type, otherwise false
     */
    public browserTypeMatched(targetScope: PageRequestTargetScope): boolean {
        if (!targetScope.browser || !targetScope.browser.browserType) {
            return true;
        }

        if (this.currentTargetScope.browser && this.currentTargetScope.browser.browserType === targetScope.browser.browserType &&
            (!targetScope.browser.version || this.currentTargetScope.browser.version === targetScope.browser.version)) {
            return true;
        }

        return false;
    }

    /**
     * function to check if current device type matches target device type
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope device type matches with config target scope device type, otherwise false
     */
    public deviceTypeMatched(targetScope: PageRequestTargetScope): boolean {
        return !targetScope.deviceFormFactor || this.currentTargetScope.deviceFormFactor === targetScope.deviceFormFactor;
    }

    /**
     * Check if current domain matches target domain
     * @param targetScope - experience config reference that has targetscope as child property
     * @return Whether the current target scope domain matches with config target scope domain
     */
    public domainMatched(targetScope: PageRequestTargetScope): boolean {
        return !targetScope.domain || this.currentTargetScope.domain === targetScope.domain;
    }

    /**
     * function to check if current experiment id matches with the requested scope experiment id.
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope experiment matches with config target scope experiment, otherwise false
     */
    public experimentMatched(targetScope: PageRequestTargetScope): boolean {
        this.currentTargetScope.pageExperiments = this.currentTargetScope.pageExperiments || [];

        if (!isString(targetScope.experimentId) || isEmpty(targetScope.experimentId)
            || this.currentTargetScope.pageExperiments.indexOf(targetScope.experimentId) >= 0) {
            return true;
        }

        return false;
    }

    /**
     * Compare the strings case insensitive.
     * @private
     * @param {string} lhs The string to compare.
     * @param {string} rhs The string to compare against.
     * @return {number} - true if the strings equal case insensitive.
     */
    public stringsEqualCaseInsensitive(lhs: string, rhs: string): boolean {
        return (lhs || "").localeCompare(rhs || "", undefined, { sensitivity: "accent" }) === 0;
    }

    /**
     * function to check if current market scope match with target scope market.
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope market matches with config target scope market, otherwise false
     */
    public marketMatched(targetScope: PageRequestTargetScope): boolean {
        // if locale is not part of target scope, we assume it is applicable for all locale.
        // Separated out this if condition to have clarity about this behavior.
        if (!targetScope.locale) {
            return true;
        }

        const targetLocale = targetScope.locale;
        const currentLocale = this.currentTargetScope.locale;

        if ((!targetLocale.language || this.stringsEqualCaseInsensitive(targetLocale.language, currentLocale.language)) &&
            (!targetLocale.market || this.stringsEqualCaseInsensitive(targetLocale.market, currentLocale.market)) &&
            (!targetLocale.script || this.stringsEqualCaseInsensitive(targetLocale.script, currentLocale.script))) {
            return true;
        }

        return false;
    }

    /**
     * function to check if current operating system type matches target operating system type
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope operating system type matches with config target scope operating system type, otherwise false
     */
    public osMatched(targetScope: PageRequestTargetScope): boolean {
        return !targetScope.os || this.currentTargetScope.os === targetScope.os;
    }

    /**
     * function to check if current pagetype matches target scope
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope pagetype matches with config target scope pagetype, otherwise false
     */
    public pageTypeMatched(targetScope: PageRequestTargetScope): boolean {
        return !targetScope.pageType || this.currentTargetScope.pageType === targetScope.pageType;
    }

    /**
     * function to check if current scope platform type matches target scope platform type
     * @private
     * @param {PageRequestTargetScope} targetScope - experience config reference that has targetscope as child property
     * @return boolean - true if current target scope platform matches with config target scope platform, otherwise false
     */
    public platformMatched(targetScope: PageRequestTargetScope): boolean {
        return !targetScope.platform || this.currentTargetScope.platform === targetScope.platform;
    }

    /**
     * Customizer for merge. We are explcitly overriding the array from taget to source. No array elements from source are preserved.
     * @private
     * @param {any} srcValue - source object to merge. Can be of type any
     * @param {any} objValue - target object to merge. Can be of type any
     * @return target Object when the type is array. Otherwise uses default behavior of merge.
     */
    public customizer(srcValue: any, objValue: any): any {
        if (isArray(srcValue)) {
            return objValue;
        }
    }

    /**
     * Function to clear internal state map for Unit testing.
     * @public
     * @void
     */
    public clearInternalState(): void {
        this.configMap.clear();
        this.initializeIndexedDb.cache.clear();
        this.populateConfigMapFromIndexedDbAsync.cache.clear();
    }

    /**
     * Function to override current request target scope. Should be used only when running outside the
     * context of client side rendering. Example tooling.
     * This function should not be called by any client rendering experience running inside the Page Context.
     * @public
     * @param {PageRequestTargetScope} targetScope - Target scope to override current Request target scope
     * @void
     */
    public overrideCurrentRequestTargetScope(targetScope: PageRequestTargetScope): void {
        this.currentTargetScopeOverride = targetScope;
        if (this.currentTargetScopeOverride) {
            this.currentTargetScope = this.currentTargetScopeOverride;
        }
    }

    /**
     * Function to override current request environment. Should be used only when not running outside the
     * context of client side rendering. Example tooling
     * @public
     * @param {Environment} overrideEnv - environment enum to override
     * @void
     */
    public overrideCurrentRequestEnvironment(overrideEnv: Environment): void {
        // fallback as prod
        this.env = overrideEnv || Environment.prod;
    }

    /**
     * Overrides a config entry. Use for local testing only. Does not work on PROD environment.
     * @param relativeUrl The config index URL to override
     * @param experienceType The experience type
     * @param config The config to use
     */
    public overrideConfig(configId: string | ConfigReference, experienceType: string, config: any): void {

        if (this.env !== Environment.prod) {

            const overrideKey = (typeof configId === "string")
                ? configId
                : getConfigRefString(configId);

            this.localOverrideMap[overrideKey] = {
                experienceType: experienceType,
                properties: config,
                typeVersion: "1.0.0",
                _name: "index"
            };
        }
    }

    /**
     * Gets the root entry point config for the current app context. This transparently handles
     * the entry point config for both CMS and Git based configs.
     * @param cmsHref - The CMS based HREF for the app config. This is here to support backwards compatibility
     *  with CMS based configs when Git configs are not enabled.
     * @returns The config for the root entry point experience from which to start page rendering.
     */
    public async getRootConfig(cmsHref: string): Promise<ExperienceConfigWrapper> {

        if (!appEnvironment.IsGitConfigs) {
            return await this.getConfig(cmsHref);
        }

        const appConfigDoc = await this.getConfig(getRootConfigRef());
        const appConfig = appConfigDoc.properties as AppConfig;
        return await this.getConfig(appConfig.rootExperience.configRef);
    }

    /**
     * Returns the resolved merged Experience config when passed Experience Config Index.
     * This function downloads the config index, filter based on target scope, download the configs and
     * merge the configs
     * @public
     * @param {string | ConfigReference} configId - Experience Index document relative url or configRef object
     * @return Promise<ExperienceConfigWrapper>
     */
    public async getConfig(configId: string | ConfigReference): Promise<ExperienceConfigWrapper> {

        if (!this.currentTargetScope) {
            return Promise.reject(new Error(`getConfig: Invalid target scope when fetching config ${JSON.stringify(configId)}`));
        }

        const startGetConfig = performance.now();

        // If this is Git configs treatment, return config object from Git config resolver.
        // Do not use cached response from this file.
        if (appEnvironment.IsGitConfigs) {
            if ((typeof configId === "string")) {
                const message = "Invalid config Id for Git configs flight";
                this.resolverErrors.push({
                    ...GitConfigsInvalidConfigRef,
                    message,
                    pb: {
                        ...GitConfigsInvalidConfigRef.pb,
                        customMessage: configId
                    }
                });
                return Promise.reject(new Error(`${message}: ${configId}`));
            }

            if (!this.configResolver) {
                const message = "Invalid configResolver object for Git configs flight";
                this.resolverErrors.push({
                    ...GitConfigsInvalidConfigResolver,
                    message,
                    pb: {
                        ...GitConfigsInvalidConfigResolver.pb,
                        customMessage: JSON.stringify(configId)
                    }
                });
                return Promise.reject(new Error(`${message}: ${JSON.stringify(configId)}`));
            }

            const configIdStr = getConfigRefString(configId);
            const localOverride = this.localOverrideMap[configIdStr];
            if (localOverride) {
                return localOverride;
            }

            this.addConfigTiming(configId.experienceType, startGetConfig, "network");
            const configEndMeasure = this.perfTimeline && this.perfTimeline.startMeasure("config", configIdStr).endMeasure;
            let config: ExperienceConfigWrapper;
            let gitConfigFromMemory: ExperienceConfig;

            try {

                gitConfigFromMemory = this.configResolver.tryGetConfigSync(configId);
                const gitConfig = gitConfigFromMemory || await this.configResolver.getConfig(configId);

                if (!gitConfig) {
                    return Promise.reject(new ConfigFetchError(`Could not fetch Git config id ${JSON.stringify(configId)}`, this.configResolver.crsHadNetworkError));
                }

                return config = {
                    experienceType: configId.experienceType,
                    properties: gitConfig.properties || {},
                    typeVersion: "Git",
                    _name: getConfigRefString(configId)
                };

            } finally {
                if (configEndMeasure) {
                    const configSource = (gitConfigFromMemory && "memory") || (config && "network") || "error";
                    configEndMeasure(`${configIdStr}-${configSource}`);
                }
            }
        }

        const relativeUrl = configId as string;

        if (!relativeUrl || !isString(relativeUrl) || isEmpty(relativeUrl)) {
            throw new Error("getConfig: Invalid relativeUrl");
        }

        // If there is a local override for testing, use it.
        const localOverride = this.localOverrideMap[relativeUrl];
        if (localOverride) {
            return localOverride;
        }

        const cacheKey = this.getCacheKey(relativeUrl);

        // if it exists in memory, return it immediately
        const configFromMemory = this.configMap.get(cacheKey);
        if (configFromMemory) {
            this.addConfigTiming(configFromMemory.experienceType, startGetConfig, "cache");
            return configFromMemory;
        }

        // if promiseMap has the item, we already have an active promise which is not yet resolved/rejected. Return this promise
        const existingPromise = this.promiseMap.get(cacheKey);
        if (existingPromise) {
            return existingPromise;
        }

        let processed = false;
        const configEndMeasure = this.perfTimeline && this.perfTimeline.startMeasure("config", relativeUrl).endMeasure;
        let experienceType: string;
        let configSource: string;

        const configPromise = new Promise<ExperienceConfigWrapper>(async (resolve, reject) => {

            try {
                // Fetch merged config from server cache if available
                if (this.useServerCache) {

                    let hasCacheError = false;
                    let isHeadConfig = false;

                    const cachedOnServer: boolean = await ServerCache.has(cacheKey);
                    if (cachedOnServer) {
                        // Retrieve and deserialize the stringified config stored in ServerCache
                        const serializedConfig: string = await ServerCache.get(cacheKey);
                        const configFromServerCache = serializedConfig ? JSON.parse(serializedConfig) as ExperienceConfigWrapper : null;
                        if (configFromServerCache) {
                            this.configTimings.push({
                                experienceType: configFromServerCache.experienceType,
                                cacheFetchDuration: performance.now() - startGetConfig
                            });

                            // This is true for head configs in multi-config fetch
                            isHeadConfig = !!(configFromServerCache.relatedConfigIndexHref);

                            processed = true;
                            this.promiseMap.delete(cacheKey);
                            resolve(configFromServerCache);

                            // Update config in server cache if this is already stale (5 minutes)
                            // This will be fire and forget internally so this resolver might last longer than expected
                            if (this.isServerCacheConfigStale(configFromServerCache.storageTimestamp)) {
                                await this.updateServerCache(relativeUrl, configFromServerCache);
                            }
                        } else {
                            logger.log(`Resolver: cacheKey=${cacheKey} Undefined from server cache`);
                            hasCacheError = true;
                        }
                    }

                    // Log all config activity for server side so this could be output as metrics.
                    logger.logObjects({
                        serverCacheGetConfig: {
                            cacheKey: cacheKey,
                            isHeadConfig: isHeadConfig,
                            isCached: cachedOnServer,
                            hasCacheError: hasCacheError
                        }
                    });

                    // Throw error when failed to fetch config from server cache even when it exists.
                    // This is usually due to really busy IPC traffic ie Server Cache message timing out communication to main process.
                    if (cachedOnServer && hasCacheError) {
                        const message = "Sever Cache key exists but server cache is undefined. Usually due to timeout when server cache IPC is busy.";
                        const detailedMessage = `cacheKey=${cacheKey}`;
                        const error = new Error(message);
                        Telemetry.sendAppErrorEvent({
                            ...SSRAppErrors.ServerCacheFetchFailed,
                            message: message,
                            pb: {
                                ...SSRAppErrors.ServerCacheFetchFailed.pb,
                                customMessage: detailedMessage
                            }
                        });

                        // Throw the error so the request would 500 instead of returning back results.
                        throw error;
                    } else if (cachedOnServer) {
                        // Already resolved the cached config. End promise.
                        return;
                    }

                    // Not found on server cache so getting config from network
                    const networkReadPromise = this.getConfigFromNetwork(relativeUrl);

                    // Await on the network promise to get the merged config.
                    const networkConfigsMap = await networkReadPromise;
                    const networkConfig = networkConfigsMap.get(cacheKey);
                    experienceType = networkConfig.experienceType;
                    this.addConfigTiming(experienceType, startGetConfig, configSource = "network");
                    resolve(networkConfig);

                } else {
                    // this will contain both the IDB cache read promise, in case we haven't yet read from
                    // IDB yet, as well as the network read promise, which we will race until one finishes.
                    const readPromises: Array<Promise<any>> = [

                        // it's possible that the IDB read is still going on, so see if we can get results from
                        // cache after racing with it. Note that we never want to 'hard await' on the IDB read
                        // as it's possible the IDB read may have errored out / stalled, etc. So we're using this
                        // result only as a secondary fallback. Primary is still the network.
                        this.populateConfigMapFromIndexedDbAsync()
                    ];

                    const networkReadPromise = this.getConfigFromNetwork(relativeUrl);
                    readPromises.push(networkReadPromise as Promise<any>);

                    // start the race between network and IDB cache read
                    await Promise.race(readPromises);

                    // after the race check in-memory again (in case the IDB read finished populated the
                    // in-memory map early)
                    const configFromMemoryAfterRace = this.configMap.get(cacheKey);
                    if (configFromMemoryAfterRace) {
                        experienceType = configFromMemoryAfterRace.experienceType;
                        this.addConfigTiming(experienceType, startGetConfig, configSource = "cache");
                        resolve(configFromMemoryAfterRace);
                        return;
                    }

                    // Await on the network promise to get the merged config.
                    const networkConfigsMap = await networkReadPromise;
                    const networkConfig = networkConfigsMap.get(cacheKey);
                    experienceType = networkConfig.experienceType;
                    this.addConfigTiming(experienceType, startGetConfig, configSource = "network");
                    resolve(networkConfig);

                }
            } catch (error) {
                this.resolverErrors.push({
                    ...ResolverAppErrors.GetConfigException,
                    message: "Exception in getConfig",
                    pb: {
                        ...ResolverAppErrors.GetConfigException.pb,
                        customMessage: `Relative Url: ${relativeUrl}. Error ${error.message || error}`
                    }
                });

                // If we don't explicitly catch the error, then the promise never gets rejected which might cause issues during experience load time.
                reject(error);
            } finally {

                processed = true;
                this.promiseMap.delete(cacheKey);

                // only measure the network operations (otherwise there are too many overlapping measures to make sense)
                if (configEndMeasure) {
                    configEndMeasure(`${experienceType}-${relativeUrl}-${configSource}`);
                }
            }
        });

        // if caller is using await then this request has been processed and we should not add it to promiseMap.
        // if caller is trying to download multiple configs in parallel, then processed should be false.
        if (!processed) {
            this.promiseMap.set(cacheKey, configPromise);
        }

        return configPromise;
    }
    /**
     * Returns the resolved merged Experience config when passed Experience Config Index.
     * This function downloads the config index, filter based on target scope, download the configs and
     * merge the configs
     * @private
     * @param {string} relativeUrl - Expeience Index document relative url.
     * @param {boolean} sendRefreshError - True if server cache refresh error should be sent immediately. False by default to push to resolverErrors.
     * @return Promise<Map<string, ExperienceConfigWrapper>> map of config(s) resolved by service/cdn.
     */
    public async getConfigFromNetwork(relativeUrl: string, sendRefreshError = false): Promise<Map<string, ExperienceConfigWrapper>> {

        const cacheKey = this.getCacheKey(relativeUrl);

        const mergedConfigsMap = new Map<string, ExperienceConfigWrapper>();

        if (this.enableConfigService) {
            const configServiceRelativeUrl = this.removeCmsPathFromRelativeUrl(relativeUrl) + this.configServiceParameters;
            try {
                // Get merged config from config service with multi config support.
                if (this.isMultiConfigRetrievalEnabled) {
                    const crsResponse = await this.downloadFile<ExperienceConfigs>(new URL(`${configServiceRelativeUrl}${this.configServiceBaseUrl.search}`, this.configServiceBaseUrl), true);
                    if (crsResponse && crsResponse.rootConfigIndexId && crsResponse.configs) {
                        const configs = Object.keys(crsResponse.configs).map(e => crsResponse.configs[e]);
                        for (const crsMergedConfig of configs) {
                            if (crsMergedConfig && crsMergedConfig.configIndexHref) {
                                const configIndexKey = crsMergedConfig.configIndexHref;
                                const isHeadConfig = configIndexKey === relativeUrl;
                                // Save config to in-memory cache on client or server cache on server.
                                // Only track relative config hrefs for head config since only need it to trigger freshness update for the others.
                                await this.addConfigToConfigMap(configIndexKey, crsMergedConfig, isHeadConfig ? configs : null);
                                mergedConfigsMap.set(this.getCacheKey(configIndexKey), crsMergedConfig);

                                if (canUseDOM()) {
                                    logger.log(`Resolver: ${configIndexKey} config for ${crsMergedConfig.experienceType} fetched from network using CRS url with multi config support.`);
                                }
                            }
                        }
                        return mergedConfigsMap;
                    }
                } else {
                    // Get single merged config from config service
                    const crsConfig = await this.downloadFile<ExperienceConfigWrapper>(new URL(`${configServiceRelativeUrl}${this.configServiceBaseUrl.search}`, this.configServiceBaseUrl), true);
                    if (crsConfig) {
                        // Save config to in-memory cache
                        await this.addConfigToConfigMap(relativeUrl, crsConfig);
                        mergedConfigsMap.set(cacheKey, crsConfig);
                        if (canUseDOM()) {
                            logger.log(`Resolver: ${cacheKey} config for ${crsConfig.experienceType} fetched from network using CRS url.`);
                        }
                        return mergedConfigsMap;
                    }
                }
            } catch (error) {
                const message = `CRS fetch failed for ${relativeUrl}.`;
                const detailedMessage = `CRS relative Url: ${configServiceRelativeUrl} Error: ${error}`;
                if (sendRefreshError) {
                    Telemetry.sendAppErrorEvent({
                        ...SSRAppErrors.ServerCacheRefreshFailed,
                        message: message,
                        pb: {
                            ...SSRAppErrors.ServerCacheRefreshFailed.pb,
                            customMessage: detailedMessage
                        }
                    });
                } else {
                    this.resolverErrors.push({
                        ...ResolverAppErrors.CrsFetchError,
                        message: message,
                        pb: {
                            ...ResolverAppErrors.CrsFetchError.pb,
                            customMessage: detailedMessage
                        }
                    });
                }
            }
        }

        // Get config from CDN when config service is not enabled or it returned null or invalid response.
        if (mergedConfigsMap && !mergedConfigsMap.has(cacheKey)) {
            const cdnConfig = await this.downloadMergeConfig(relativeUrl);
            if (cdnConfig) {

                // Save config to in-memory cache
                await this.addConfigToConfigMap(relativeUrl, cdnConfig);

                mergedConfigsMap.set(cacheKey, cdnConfig);
                if (canUseDOM()) {
                    logger.log(`Resolver: ${cacheKey} config fetched from network using Akamai url.`);
                }

                // Dev scenario: if config service is not enabled and we got configs from CDN, do not log app error.
                if (this.enableConfigService) {
                    this.resolverErrors.push({
                        ...ResolverErrorConfigsDownloadedByCdn,
                        message: "Configs served from CDN after CRS fetch failed",
                        pb: {
                            ...ResolverErrorConfigsDownloadedByCdn.pb,
                            customMessage: `Relative Url: ${relativeUrl} CRS Params: ${this.configServiceParameters}`
                        }
                    });
                }
            }
        }

        return mergedConfigsMap;
    }

    /**
     * Set configMap object with appropriate values.
     * @private
     * @param configIndexHref example: cms/api/amp/experienceConfigIndex/AfCDeG
     * @param config object of ExperienceConfigWrapper
     * @param multiConfigs optional array of related ExperienceConfigWrapper due to multi config support from CRS (server only)
     */
    public async addConfigToConfigMap(configIndexHref: string, config: ExperienceConfigWrapper, multiConfigs?: ExperienceConfigWrapper[]): Promise<void> {
        config.storageTimestamp = this.stalenessCheckTimestamp;
        config.configIndexHref = configIndexHref;

        const cacheKey = this.getCacheKey(configIndexHref);

        // No need to cache on configMap on server side since it will be another layer of memcache to worry about.
        if (this.useServerCache) {

            // Update related config href if passed in.
            if (multiConfigs) {
                config.relatedConfigIndexHref = multiConfigs.map(c => c.configIndexHref);
            }

            // Save config to server cache so other processes could reuse the cache.
            // ServerCache only accepts string to avoid serialization issues.
            // TTL is 1 day for now.
            const serializedConfig = JSON.stringify(config);
            await ServerCache.set(cacheKey, serializedConfig, SERVER_CACHE_TTL);
        } else {
            // Set configMap for future reference.
            this.configMap.set(cacheKey, config);
        }
    }

    /**
     * Refresh and update config when stale for server cache in order to reduce overall network call from server side.
     * @private
     * @param configIndexHref example: cms/api/amp/experienceConfigIndex/AfCDeG
     * @param configFromServerCache object of ExperienceConfigWrapper
     */
    public async updateServerCache(configIndexHref: string, configFromServerCache: ExperienceConfigWrapper) {

        // Server only sanity check
        if (!this.useServerCache) {
            return;
        }

        try {
            // Skip refresh config if it is already in the promise map
            if (!this.refreshServerCachePromiseMap.get(configIndexHref)) {

                // Refresh config and related config first so other processes don't trigger network call as well.
                // This is not perfect as this is not atomic so there might be race condition which doesn't really cause harm except multiple network calls might happen.
                // Better implementation would be separate worker thread to deal with refreshes but more complicated implementation.
                if (configFromServerCache.relatedConfigIndexHref) {
                    await Promise.all(configFromServerCache.relatedConfigIndexHref.map(x => {
                        const refreshPromise = (x === configIndexHref) ?
                            this.refreshConfigServerCache(configIndexHref, configFromServerCache) :
                            this.refreshConfigServerCache(x);

                        this.refreshServerCachePromiseMap.set(x, refreshPromise);
                        return refreshPromise;
                    }));

                    // Remove from promise map after all refreshes are done.
                    for (let i = 0; i < configFromServerCache.relatedConfigIndexHref.length; i++) {
                        this.refreshServerCachePromiseMap.delete(configFromServerCache.relatedConfigIndexHref[i]);
                    }
                } else {
                    const refreshPromise = this.refreshConfigServerCache(configIndexHref, configFromServerCache);
                    this.refreshServerCachePromiseMap.set(configIndexHref, refreshPromise);
                    await refreshPromise;
                    this.refreshServerCachePromiseMap.delete(configIndexHref);
                }

                // Update config from network again since this is now stale.
                //
                // Note that there's no await here since we do want to fire and forget config update on server cache
                // so we could serve next request asap.
                // Closure for "this" scope is maintained but not any global singleton;
                // so if next request starts before updateServerCache promise is done, singletons could be reset.
                // Fortunately, only logger and app errors are called and not appEnvironment.
                this.getConfigFromNetwork(configIndexHref);
            }
        } catch (error) {
            const message = `Update server cache failed for ${configIndexHref}.`;
            const detailedMessage = `Error: ${error}`;

            Telemetry.sendAppErrorEvent({
                ...SSRAppErrors.ServerCacheRefreshFailed,
                message: message,
                pb: {
                    ...SSRAppErrors.ServerCacheRefreshFailed.pb,
                    customMessage: detailedMessage
                }
            });
        }
    }

    /**
     * Refresh config staleness WITHOUT changing the value on server cache
     * so that other processes don't need to fetch config from network at the same time.
     * @private
     * @param configIndexHref example: cms/api/amp/experienceConfigIndex/AfCDeG
     * @param configFromServerCache object of ExperienceConfigWrapper
     */
    private async refreshConfigServerCache(configIndexHref: string, configFromServerCache?: ExperienceConfigWrapper): Promise<void> {

        // Server only sanity check
        if (!this.useServerCache) {
            return;
        }

        const cacheKey = this.getCacheKey(configIndexHref);

        if (!configFromServerCache) {
            const serializedConfig: string = await ServerCache.get(cacheKey);
            configFromServerCache = serializedConfig ? JSON.parse(serializedConfig) as ExperienceConfigWrapper : null;
        }

        if (configFromServerCache) {
            // Update staleness timestamp to current request and set to server cache
            configFromServerCache.storageTimestamp = this.stalenessCheckTimestamp;
            const serializedConfig = JSON.stringify(configFromServerCache);
            await ServerCache.set(cacheKey, serializedConfig, SERVER_CACHE_TTL);
        }
    }

    /**
     * Clear render cache when applicable.
     * @private
     * @param areConfigsEqual: response from web worker. This is typically true or false in this case.
     */
    public purgeRenderCacheUponConfigChanges(areConfigsEqual: boolean): void {
        if (areConfigsEqual) {
            return;
        }

        try {
            getLocalStorage().removeSubstringKeys("lastKnown_");
            getLocalStorage().removeSubstringKeys("timestamp_");
            getLocalStorage().removeSubstringKeys("keys_");
        } catch (e) {
            // Send alert to monitoring system that render cache is not being purged, when it is supposed to.
            this.resolverErrors.push({
                ...ResolverAppErrors.FailedToPurgeRenderCache,
                message: "Failed to purge render cache when cached config and network config is not same.",
                pb: {
                    ...ResolverAppErrors.FailedToPurgeRenderCache.pb,
                    customMessage: e
                }
            });
        }
    }

    /**
     * Get the config over the network, merge and return.
     * @private
     * @param {string} relativeUrl - Expeience Index document relative url.
     * @return Promise<ExperienceConfig>
     */
    public async downloadMergeConfig(relativeUrl: string): Promise<ExperienceConfigWrapper> {
        const experienceIndex = await this.downloadFile<IExperienceConfigIndex>(new URL(`${relativeUrl}${this.cdnConfigBaseUrl.search}`, this.cdnConfigBaseUrl), false);
        const configPaths = this.resolveConfigIndex(experienceIndex);
        const configObjects = await this.downloadExperienceConfig(this.cdnConfigBaseUrl, configPaths);
        const config: ExperienceConfigWrapper = this.mergeConfig(configObjects) as ExperienceConfigWrapper;
        config.experienceType = experienceIndex.experienceType;

        return config;
    }

    /**
     * Given an array of config urls, this function downloads them in parallel and returns a promise.
     * @private
     * @param {string} baseConfigUrl - base config url depends on implementation
     * @param {string[]} hrefs - Array of configuration relative urls
     * @return Promise<ExperienceConfig[]>
     */
    public async downloadExperienceConfig(baseConfigUrl: URL, hrefs: string[]): Promise<IExperienceConfig[]> {
        const promiseArray = [];
        hrefs.forEach(href => {
            promiseArray.push(this.downloadFile<IExperienceConfig>(new URL(`${href}${baseConfigUrl.search}`, baseConfigUrl), false));
        });

        return await Promise.all(promiseArray);
    }

    /**
     * downloads file from CMS given the relative url
     * @private
     * @param {string} relativeUrl - relative url of the document
     * @param {boolean} isCrsFetch - Is this CRS fetch
     * @return Promise<T> returns generic Type object wrapped in Promise
     */
    public async downloadFile<T>(url: URL, isCrsFetch: boolean): Promise<T> {
        // Use node-fetch to be compatible with SSR
        // Default timeout is 10 seconds client side, 900ms server side
        const timeoutInMs = canUseDOM() ? CSRTimeoutInMs : SSRTimeoutInMs;
        const fetchUrl = url.toString();
        return abortableFetch(fetchUrl, timeoutInMs)
            .then(response => {
                if (response.ok) {
                    return response.json();
                }

                let errorMessage = "";
                if (isCrsFetch) {
                    const crsVersion = response.headers.get("X-Crs-BuildVersion") || "";
                    const msedgeRef = response.headers.get("x-msedge-ref") || "";
                    errorMessage = `msedge ref: ${msedgeRef} Status: ${response.status}, Status Text: ${response.statusText}, Url: ${fetchUrl} and CRS Version: ${crsVersion}`;
                    this.resolverErrors.push(createError(ConfigCrsFetchNon200Response, "CRS sent non-200 response", errorMessage));
                } else {
                    errorMessage = `Status: ${response.status}, Status Text: ${response.statusText}, Url: ${fetchUrl}`;
                    this.resolverErrors.push(createError(ConfigCdnFetchNon200Response, "Cdn config fetch received non-200 response", errorMessage));
                }

                return Promise.resolve();
            });
    }

    /**
     * This function generates a key that is used to write/read configurations to/from storage.
     * example key: "kids|ja|jp|testPage|mmx"
     * @private
     * @return string
     */
    public generateKey(): string {

        // Client side resolver will use reduced set of keys for perf and retrival logic. This logic has to match WebWorkerResolver.generateKey
        if (canUseDOM()) {

            const { audienceMode = "", locale = {}, pageType = "" } = this.currentTargetScope || {};
            const { language, market } = locale;

            const keys = [audienceMode, language, market, pageType, appEnvironment.AppType || ""];
            return keys.map(key => key || "").join("|").toLowerCase();

            // Server side resolver needs to generate full set of target scopes since multiple pages/users will share the same cache for longer duration
        } else {
            let { audienceMode = "",
                browser = null,
                deviceFormFactor = "",
                experimentId = "",
                locale = {},
                os = {},
                pageType = "",
                platform = "",
                pageExperiments = null } = this.currentTargetScope || {};
            const { language, market } = locale;
            const { browserType = "", version = "" } = browser || {};

            // Filter out non-peregrine flights so they are not part of cache key.
            pageExperiments = getPeregrineFlights(pageExperiments);

            const keys = [audienceMode, browserType, version, deviceFormFactor, language, market, os, experimentId, pageType, platform, pageExperiments ? pageExperiments.join() : "", appEnvironment.AppType || ""];
            return keys.map(key => key || "").join("|").toLowerCase();
        }
    }

    /**
     * Function checks if ConfigService is enabled.
     * @returns true if supported else false.
     */
    public useConfigService(): boolean {
        const urlQuery = getLocationSearch();
        if (urlQuery) {
            const param = UrlUtility.getQueryParameterByName("enableConfigService", urlQuery);
            if (param) {
                return param.toLocaleLowerCase() === "true";
            }
        }

        // PCS flag to turn on/off ConfigService.
        // When EnableConfigService = false, it gets configs from CDN and merge at client side.
        return appEnvironment.EnableConfigService;
    }

    /**
     * Function that returns maxDepth if exists in query string else default value.
     * This is the max depth of the config tree.
     * @returns Depth of config in tree.
     */
    public getConfigRetrievalMaxDepth(): string {
        const param = UrlUtility.getQueryParameterByName("maxDepth", getLocationHref());
        if (param) {
            return param;
        }

        // PCS flag for maxDepth
        if (appEnvironment.FeatureFlags
            && appEnvironment.FeatureFlags.crsMaxDepth) {
            return appEnvironment.FeatureFlags.crsMaxDepth;
        }

        // Config service to use this default to parse depth of config tree.
        return "10";
    }

    /**
     * Function checks if url has any dev only query string param
     * @returns true if exist else false.
     */
    public containsDevOnlyQueryParameters(): boolean {
        const urlQuery = getLocationSearch();
        const item = UrlUtility.getQueryParameterByName("item", urlQuery);
        if (item && item.length > 0) {
            return true;
        }

        const segment = UrlUtility.getQueryParameterByName("pcsonly", urlQuery);
        if (segment && segment.length > 0) {
            return true;
        }

        return false;
    }

    /**
     * Returns resolver errors if any.
     * @returns resolverErrors
     */
    public getResolverErrors(): AppError[] {
        return this.resolverErrors;
    }

    /**
     * Refresh configs in indexedDb. Next time users visits the page within 3 days, we will get configs from indexedDB first
     * After TTVR we will background refresh them in web worker through this function.
     * If it more than 3 days, we won't use indexedDb response in main thread, After TTVR, we will background refresh.
     * This is required to have latest configs in indexedDb
     */
    public async refreshConfigCache(): Promise<void> {
        // If web worker is not enabled, do not refresh.
        if (!this.useWebWorkerToCache) {
            return;
        }

        try {
            // Send post message to web worker to refresh indexedDB.
            const wwResponse = await getFromWebWorker({ id: "refreshconfigs" });
            if (wwResponse
                && wwResponse.fetched
                && wwResponse.payload !== undefined
                && wwResponse.payload !== null) {

                // Receive payload as post message response containing whether configs are changed and live cache busting identifier.
                const refreshConfigResponse = <RefreshConfigsResponse>wwResponse.payload;

                // Check if cached config and network config properties have any changes.
                this.purgeRenderCacheUponConfigChanges(refreshConfigResponse.configsEqual);

                // Git configs treatment: write config hashes to local storage.
                this.writeConfigCacheProperties();

                // For Flight specific config resolution, we want to use cached configs only if flights match.
                try {
                    getLocalStorage().setItem(this.peregrineFlightsKey, this.peregrineFlights);
                } catch (e) {
                    this.resolverErrors.push({
                        ...ResolverAppErrors.PrgFlightLsWriteError,
                        message: "Error writing prg flights to ",
                        pb: {
                            ...ResolverAppErrors.PrgFlightLsWriteError.pb,
                            customMessage: e
                        }
                    });
                }

                // Web worker queried CRS endpoint and got value latest liveCbId.
                // Write CBID into local storage.
                if (refreshConfigResponse.liveCbId) {
                    const liveCbid = refreshConfigResponse.liveCbId.toString();
                    this.writeCbidInLocalStorage(liveCbid);

                    const pcsCbid = appEnvironment.CacheBustingIdentifier || "";
                    const highestCbid = window._cbid || "";
                    if (!appEnvironment.IsGitConfigs
                        && (liveCbid !== pcsCbid
                        || liveCbid !== highestCbid)) {
                        this.resolverErrors.push({
                            ...ResolverAppErrors.DifferentCbid,
                            message: "Different Cbid detected than configured.",
                            pb: {
                                ...ResolverAppErrors.DifferentCbid.pb,
                                customMessage: `PcsCbid = ${pcsCbid}. HighestCbid = ${highestCbid}. LiveCbid = ${liveCbid}.`
                            }
                        });
                    }
                }
            }
        } catch (e) {
            this.resolverErrors.push({
                ...ResolverAppErrors.RefreshConfigCacheFailed,
                message: "refreshConfigCache error",
                pb: {
                    ...ResolverAppErrors.RefreshConfigCacheFailed.pb,
                    customMessage: e
                }
            });
        }
    }

    /**
     * Function checks whether to enable or disable indexedDb in main using query string override.
     * @returns true if supported else false.
     */
    public enableIndexedDbInMainResolver(): boolean {
        const urlQuery = getLocationSearch();
        if (urlQuery) {
            const param = UrlUtility.getQueryParameterByName("enableIndexedDbInMainResolver", urlQuery);
            if (param) {
                return param.toLocaleLowerCase() === "true";
            }
        }

        return true;
    }

    /**
     * Function to check config is stale.
     * @returns true if stale else false.
     */
    public isConfigStale(storageTimestamp: number): boolean {
        if (!storageTimestamp) {
            return true;
        }

        // 259200000 = 3 days in milliseconds
        if ((this.stalenessCheckTimestamp - storageTimestamp) > 259200000) {
            return true;
        }

        return false;
    }

    /**
     * Function to check config on server cache is stale
     * @returns true if stale else false.
     */
    public isServerCacheConfigStale(storageTimestamp: number): boolean {
        if (!storageTimestamp) {
            return true;
        }

        // 300000 = 5 minutes in milliseconds
        if ((this.stalenessCheckTimestamp - storageTimestamp) > SERVER_CACHE_CONFIG_STALENESS_TIMEOUT) {
            return true;
        }

        return false;
    }

    /**
     * Removes relative path from url and returns Config Index Href.
     * Input Example: cms/api/amp/experienceConfigIndex/ABCD
     * @returns string. Example: ABCD
     */
    public removeCmsPathFromRelativeUrl(relativeUrl: string): string {
        return relativeUrl.replace("cms/api/amp/experienceConfigIndex/", "");
    }

    /**
     * Removes cached config from IndexedDB.
     */
    public removeCachedConfig(relativeUrl: string): void {
        // check if web worker and indexedDb use is allowed.
        if (this.useWebWorkerToCache && this.useIndexedDb) {
            // Send post message to web worker.
            sendToWebWorker({ id: "clearconfigcache", payload: relativeUrl });
        } else {
            // If web worker is not allowed, remove all configs from indexedDb as they are all stored as a single row.
            this.clearConfigCache();
        }
    }

    /**
     * This helper function is used to clear cached configs
     * @returns Promise<void>
     */
    public async clearConfigCache(): Promise<void> {
        if (this.useIndexedDb) {
            try {
                await this.initializeIndexedDb();

                try {
                    await this.indexedDb.clearObjects();
                    logger.log("Resolver: Cleared all configs from indexedDb.");
                } catch {
                    logger.logError("Resolver: Failed to clear all configs from indexedDb");
                    this.useIndexedDb = false;
                }
            } catch {
                logger.logError("Resolver: Failed to initialize indexedDb");
            }
        }
    }

    /**
     * For testing purpose to access what has been set for configServiceParameters
     */
    public getConfigServiceParameters(): string {
        return this.configServiceParameters;
    }

    /**
     * Adds an entry into the config timings for the given type and relative start time
     */
    public addConfigTiming(experienceType: string, startTime: number, type: "network" | "cache"): void {
        this.configTimings.push({
            experienceType,
            [type + "FetchDuration"]: performance.now() - startTime
        });
    }

    /**
     * Helper function to get Config Timings
     * @param pageTimings _pageTimings object
     */
    public getConfigTimings(pageTimings: any = {}, getAllConfigTimingsOverride = false): void {
        if (!this.configTimings) {
            return;
        }

        let allConfigsCacheDuration = 0;
        let allConfigsNetworkDuration = 0;
        for (const configTiming of this.configTimings) {
            // Instrument networkFetchDuration and cacheFetchDuration
            const experienceType = configTiming.experienceType.toLowerCase();
            const cacheFetchDuration = configTiming.cacheFetchDuration;
            const networkFetchDuration = configTiming.networkFetchDuration;

            if (experienceType === "entrypoint"
                || experienceType === "river"
                || experienceType === "contentpreview"
                // when appropriate debug flag is set, output all config timings.
                || appEnvironment.IsDebug
                || getAllConfigTimingsOverride) {

                // Instrumenting networkFetchDuration to check effectiveness of get Config over the network for FRE.
                if (networkFetchDuration !== undefined) {
                    pageTimings["ConfigFetch." + experienceType] = Math.round(networkFetchDuration);
                }

                // Instrumenting cacheFetchDuration to check effectiveness of storing merged config since it will be always used in non FRE.
                if (cacheFetchDuration !== undefined) {
                    pageTimings["ConfigCache." + experienceType] = Math.round(cacheFetchDuration);
                }
            }

            if (cacheFetchDuration !== undefined) {
                allConfigsCacheDuration += cacheFetchDuration;
            }

            if (networkFetchDuration !== undefined) {
                allConfigsNetworkDuration += networkFetchDuration;
            }
        }

        if (allConfigsCacheDuration) {
            pageTimings["ConfigCache.All"] = Math.round(allConfigsCacheDuration);
        }

        if (allConfigsNetworkDuration) {
            pageTimings["ConfigFetch.All"] = Math.round(allConfigsNetworkDuration);
        }
    }

    /**
     * UNIT TEST PURPOSE: Function to set configTimings
     * @param ConfigFetchTiming[]
     */
    public setConfigTimingsObject(configFetchTimings: ConfigFetchTiming[]): void {
        this.configTimings = configFetchTimings;
    }

    /**
     * Get the cache key based on confix index url and the target scope generated suffix.
     * @param relativeUrl The config url
     * @returns The unique cache key for the merged config + target scope combination.
     */
    public getCacheKey(relativeUrl: string): string {
        return relativeUrl === null ? null : this.removeCmsPathFromRelativeUrl(relativeUrl) + "_" + this.storageKey;
    }

    /**
     * Get cached Configs from IndexedDb and populate configMap based on target scope key.
     */
    private async _populateConfigMapFromIndexedDbAsync(): Promise<void> {

        let cacheSource: string;

        try {
            // Check if indexedDb is present, if not return.
            // Check if web worker is present, if not, indexedDb will not be refreshed since refreshConfigCache uses post message to web worker.
            // so you may get stale configs within 3 days of time range so this check is essential as well.
            // Check if we've previously attempted to read from IDB, and if don't do it again.
            if (!this.useIndexedDb || !this.useWebWorkerToCache) {
                return;
            }

            // For Flight specific config resolution, we want to use cached configs only if flights match.
            try {
                const prgFlights = getLocalStorage().getItem(this.peregrineFlightsKey);
                if (prgFlights
                    && this.peregrineFlights
                    && prgFlights !== this.peregrineFlights) {

                    // Reverse flight: if cached configs are for different flight, throw app error.
                    if (this.peregrineFlights.indexOf("prg-lsconfigs") >= 0) {
                        this.resolverErrors.push({
                            ...CachedConfigsForDifferentFlight,
                            message: "Cached configs are for different flight"
                        });
                    }

                    // DO NOT USE CACHED CONFIGS SINCE FLIGHTS ARE NOT SAME
                    // SO CONFIG RESOLUTION MAY LEAD TO INCONSISTENT BEHAVIOR
                    return;
                }
            } catch (e) {
                this.resolverErrors.push({
                    ...ResolverAppErrors.PrgFlightLsReadError,
                    message: "Error reading prg flights from local storage",
                    pb: {
                        ...ResolverAppErrors.PrgFlightLsReadError.pb,
                        customMessage: e
                    }
                });
            }

            const startBulkConfig = performance.now();

            this.configMap.clear();
            let cachedConfigs: CachedConfigsType;

            // Optionally pre-initialized IDB config read promise at the top page level
            const { _idbConfigReadPromise, _idbConfigLookup } = window;

            // Check if we have a top page-level initialized IDB read config lookup populated
            // with the specific entry we need, and if so use it. Otherwise proceed to load from indexDB
            cachedConfigs = (_idbConfigLookup || {})[this.storageKey] as CachedConfigsType;
            if (cachedConfigs) {

                cacheSource = "pre-initialized config cache";

            } else if (_idbConfigReadPromise) {
                // see if we have the pre-initialized config read promise from IDB, and if so
                // await on that instead of starting our own duplicate IDB init/read.

                cacheSource = "pre-initialized IndexedDb read";
                await _idbConfigReadPromise;
                cachedConfigs = (window._idbConfigLookup || {})[this.storageKey] as CachedConfigsType;

            } else {

                // start our own IDB read.
                cacheSource = "IndexedDb bulk read";
                await this.initializeIndexedDb();
                cachedConfigs = await this.indexedDb.getObject<CachedConfigsType>(this.storageKey);
            }

            if (!cachedConfigs || cachedConfigs.size === 0) {
                logger.log(`Resolver: Bulk read of configs from ${cacheSource} didn't find any configs.`);
                return;
            }

            this.populateConfigMapFromCachedConfigs(cachedConfigs, cacheSource);
            this.addConfigTiming("IdbInitAndReadAll", startBulkConfig, "cache");

        } catch (e) {
            this.resolverErrors.push({
                ...ResolverAppErrors.IndexedDbBulkReadFailed,
                message: "IndexedDb bulk read failed",
                pb: {
                    ...ResolverAppErrors.IndexedDbBulkReadFailed.pb,
                    customMessage: `IndexedDb bulk read from ${cacheSource} failed. Error: ${e}`
                }
            });
            this.useIndexedDb = false;
        }
    }

    /**
     * Initialize indexedDb for configs. Instead of using this directly, please use the memoized
     * initializeIndexedDb function instead to avoid re-entrancy of the same async operation.
     */
    private async _initializeIndexedDb(): Promise<void> {
        if (!this.useIndexedDb) {
            return;
        }

        try {
            // IMPORTANT!! The wwConfigs name must be in sync with the PCS script that initializes the IDB and WebWorkerResolver
            const { _idbConfigOpenPromise } = window;
            await this.indexedDb.initializeIndexedDb(1, "Configurations_12", "wwConfigs", _idbConfigOpenPromise);
            logger.log("Resolver: IndexedDb initialized in main thread.");
        } catch (e) {
            this.resolverErrors.push({
                ...ResolverAppErrors.IndexedDbInitFailed,
                message: "IndexedDb initialization failed",
                pb: {
                    ...ResolverAppErrors.IndexedDbInitFailed.pb,
                    customMessage: e
                }
            });
        }
    }

    /**
     * Populates the in-memory map object with the configs from cache
     */
    private populateConfigMapFromCachedConfigs(cachedConfigs: CachedConfigsType, cacheSource: string): void {

        // targetScopeKey is inserted into IndexedDb along with configIndexHref.
        // Although there can be more than type of keys being formed since DHP and NTP will have different ones due to pageType in targetScope.
        let unusableCount = 0;

        cachedConfigs.forEach(cachedConfig => {
            if (!cachedConfig.configIndexHref
                || this.isConfigStale(cachedConfig.storageTimestamp)) {

                unusableCount++;
            } else {
                const cacheKey = this.getCacheKey(cachedConfig.configIndexHref);

                this.configMap.set(cacheKey, cachedConfig);
            }
        });

        logger.log(`Resolver: Found ${cachedConfigs.size - unusableCount} valid configs out of ${cachedConfigs.size} from ${cacheSource}`);
    }

    /**
     * Get Peregrine specific flights from client settings and local storage
     * True if they are same else false
     */
    private prgFlightsSameAsLocalStorage(): boolean {
        try {
            const flightsFromStorage = getLocalStorage().getItem(this.peregrineFlightsKey);
            return flightsFromStorage
                && this.peregrineFlights
                && flightsFromStorage === this.peregrineFlights;
        } catch (error) {
            this.resolverErrors.push({
                ...ResolverAppErrors.PrgFlightLsReadError,
                message: "Error reading prg flights from localStorage",
                pb: {
                    ...ResolverAppErrors.PrgFlightLsReadError.pb,
                    customMessage: error
                }
            });
        }

        return false;
    }

    /**
     * Get local storage key where we will be storing hashes
     */
    private getConfigsCacheLocalStorageKey(): string {
        const { audienceMode, locale = {}, pageType } = this.currentTargetScope || {};
        return `config_hash_${audienceMode}_${locale.language}_${locale.market}_${pageType}_${appEnvironment.AppType}`;
    }

    /**
     * Gets the config hashes from local storage.
     * @param clientSettings - The "clientSettings" object.
     * @returns string config hash QSP
     */
    private getConfigCacheProperties(): ConfigCacheProperties {
        let configCacheProperties: ConfigCacheProperties;
        try {
            // Get config hashes from client settings
            configCacheProperties = appEnvironment.ConfigCacheProperties;

            // If user was in different flights earlier in last page than right now, no need to get hashes from local storage.
            // We do not want to use indexedDB configs.
            if (!this.prgFlightsSameAsLocalStorage()) {
                // Here we know that flights for this page are different than flights for earlier page for which we have render cache snapshot and configs saved.
                // We do not want to use RC snapshot for this and do not want to use cached configs.
                // RC snapshot will be helpful like an automatic recovery if something goes wrong.
                this.purgeRenderCacheUponConfigChanges(false);

                return configCacheProperties;
            }

            // It's possible, especially on Legacy Edge, for appEnvironment.ConfigCacheProperties to be undefined
            // due to differences with the AppCache. Also possible if upgrading to GIT configs.  We cannot default
            // it to an object as it is used in several places as "truthy", so adding a check here to make sure we have it.
            // Not returning before line 1790 to make sure we don't change the behavior of the function.
            if (configCacheProperties) {
                // Get last hashes from local storage
                const configHashes = getLocalStorage().getItem(this.getConfigsCacheLocalStorageKey());
                if (configHashes) {
                    const {bundleHash, configHash, locHash} = JSON.parse(configHashes) || {};
                    if (bundleHash && configHash && locHash) {
                        configCacheProperties.lastBundleHash = bundleHash;
                        configCacheProperties.lastConfigHash = configHash;
                        configCacheProperties.lastLocHash = locHash;
                    }
                }
            }

            return configCacheProperties;
        } catch (error) {
            logger.logError(`Error getting config hashes from ls: ${error}`);

            // TODO: Add app error
        }

        return configCacheProperties;
    }

    /**
     * Write config hashes to local storage
     */
    private writeConfigCacheProperties(): void {
        if (!appEnvironment.IsGitConfigs) {
            return;
        }

        try {
            const { bundleHash, configHash, locHash: locHash } = appEnvironment.ConfigCacheProperties || {};
            if (bundleHash && configHash && locHash) {
                getLocalStorage().setItem(this.getConfigsCacheLocalStorageKey(), JSON.stringify({
                    bundleHash,
                    configHash,
                    locHash
                }));
            }
        } catch (error) {
            logger.logError(`Error writing config hashes to ls: ${error}`);

            // TODO: Add app error
        }
    }

    /**
     * Write given CBID in local storage
     * @param cbid Cache busting identifier
     */
    private writeCbidInLocalStorage(cbid: string): void {
        const cbidKey = `cbid_${appEnvironment.AppType || ""}`;
        try {
            getLocalStorage().setItem(cbidKey, cbid);
        } catch (e) {
            throw `Error writing cbid: ${cbid} to local storage with key ${cbidKey}. Error: ${e}`;
        }
    }
}

/**
 * Config Timings type to determine how much time each experience took to getConfig
 */
interface ConfigFetchTiming {
    /**
     * Experience Type
     */
    experienceType: string,

    /**
     * Time took to get configs over the network and merge them.
     */
    networkFetchDuration?: number,

    /**
     * Time took to get merged configs from cache.
     */
    cacheFetchDuration?: number
}

/**
 * Typed object containing boolean flag that tells if configs are equal and live cbid. This type comes are response from web worker to refresh config message.
 */
interface RefreshConfigsResponse {
    /**
     * True if configs are equal
     */
    configsEqual?: boolean;

    /**
     * CBID (cache busting identifier) from live document
     */
    liveCbId?: number;
}

/** Resolver Singleton */
let _instance: Resolver = new Resolver();

/** Reinitialize the singleton class instance. */
export function resetResolverInstance(): void {
    _instance = new Resolver();
}

export const resolver = {
    getRootConfig: (cmsHref: string) => { return _instance.getRootConfig(cmsHref); },
    getConfig: (configId: string | ConfigReference) => { return _instance.getConfig(configId); },
    mergeConfig: (configs: IExperienceConfig[]) => { return _instance.mergeConfig(configs); },
    resolveConfigIndex: (configIndex: IExperienceConfigIndex) => { return _instance.resolveConfigIndex(configIndex); },
    targetScopeMatched: (experienceRef: IExperienceConfigLink) => { return _instance.targetScopeMatched(experienceRef); },
    getConfigTimings: (pageTimings: any = {}, getAllConfigTimingsOverride = false) => { return _instance.getConfigTimings(pageTimings, getAllConfigTimingsOverride); },
    getResolverErrors: () => { return _instance.getResolverErrors(); },
    refreshConfigCache: () => { return _instance.refreshConfigCache(); },
    removeCachedConfig: (relativeUrl: string) => { return _instance.removeCachedConfig(relativeUrl); },
    clearConfigCache: () => { return _instance.clearConfigCache(); },
    getInstance: () => { return _instance; },
    setTargetScopeAndConfigServiceParams: () => { return _instance.setTargetScopeAndConfigServiceParams(); },
    initialize: (appErrorCallback: (data: AppError) => void) => { return _instance.initialize(appErrorCallback); },
    getUseConfigService: () => { return _instance.useConfigService(); },

    // these are test specific exports
    overrideConfig: (configId: string | ConfigReference, experienceType: string, config: any) => { return _instance.overrideConfig(configId, experienceType, config); },
    overrideCurrentRequestTargetScope: (targetScope: PageRequestTargetScope) => { return _instance.overrideCurrentRequestTargetScope(targetScope); },
    overrideCurrentRequestEnvironment: (overrideEnv: Environment) => { return _instance.overrideCurrentRequestEnvironment(overrideEnv); },
    generateKey: () => { return _instance.generateKey(); },
    clearInternalState: () => { return _instance.clearInternalState(); },
    initializeCaching: (idb?: IndexedDb) => { return _instance.initializeCaching(idb); },
    getConfigServiceParameters: () => { return _instance.getConfigServiceParameters(); },
    setConfigTimingsObject: (configFetchTimings: ConfigFetchTiming[]) => { return _instance.setConfigTimingsObject(configFetchTimings); },
    getCacheKey: (relativeUrl: string) => { return _instance.getCacheKey(relativeUrl); },
    updateServerCache: (configIndexHref: string, configFromServerCache: ExperienceConfigWrapper) => { return _instance.updateServerCache(configIndexHref, configFromServerCache); }
};
